import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Plus,
  Check,
  AlertCircle,
  Clock,
  ChevronRight,
  Circle,
  CheckCircle2,
  Trash2,
  Edit2
} from 'lucide-react';
import { format, isPast, isToday, isTomorrow } from 'date-fns';

const TodoSlidePanel = ({
  isOpen,
  onClose,
  todos = [],
  onToggleTodo,
  onCreateTodo,
  onEditTodo,
  onDeleteTodo,
  isLoading = false
}) => {
  const [newTodoTitle, setNewTodoTitle] = useState('');
  const [isAddingTodo, setIsAddingTodo] = useState(false);
  const inputRef = useRef(null);

  // Focus input when adding mode is activated
  useEffect(() => {
    if (isAddingTodo && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAddingTodo]);

  // Close panel on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Sort todos: pending first, then by priority, then by due date
  const sortedTodos = [...todos].sort((a, b) => {
    // Completed todos go to bottom
    if (a.status === 'completed' && b.status !== 'completed') return 1;
    if (a.status !== 'completed' && b.status === 'completed') return -1;

    // Then by overdue status
    const aOverdue = a.due_date && isPast(new Date(a.due_date)) && a.status !== 'completed';
    const bOverdue = b.due_date && isPast(new Date(b.due_date)) && b.status !== 'completed';
    if (aOverdue && !bOverdue) return -1;
    if (!aOverdue && bOverdue) return 1;

    // Then by priority
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    const aPriority = priorityOrder[a.priority] ?? 3;
    const bPriority = priorityOrder[b.priority] ?? 3;
    if (aPriority !== bPriority) return aPriority - bPriority;

    // Then by due date
    if (a.due_date && b.due_date) {
      return new Date(a.due_date) - new Date(b.due_date);
    }
    if (a.due_date && !b.due_date) return -1;
    if (!a.due_date && b.due_date) return 1;

    return 0;
  });

  const pendingTodos = sortedTodos.filter(t => t.status !== 'completed');
  const completedTodos = sortedTodos.filter(t => t.status === 'completed');

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    if (!newTodoTitle.trim()) return;

    try {
      await onCreateTodo({
        title: newTodoTitle.trim(),
        priority: 'medium',
        status: 'open'
      });
      setNewTodoTitle('');
      setIsAddingTodo(false);
    } catch (err) {
      console.error('Failed to create todo:', err);
    }
  };

  const formatDueDate = (dueDate) => {
    if (!dueDate) return null;
    const date = new Date(dueDate);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'MMM d');
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-ui-red-fg bg-ui-red-bg';
      case 'medium': return 'text-ui-orange-fg bg-ui-orange-bg';
      case 'low': return 'text-ui-green-fg bg-ui-green-bg';
      default: return 'text-ui-ink-3 bg-ui-sunk';
    }
  };

  const TodoItem = ({ todo }) => {
    const isCompleted = todo.status === 'completed';
    const isOverdue = todo.due_date && isPast(new Date(todo.due_date)) && !isCompleted;
    const [showActions, setShowActions] = useState(false);

    return (
      <div
        className={`group px-3 py-2.5 border-b border-ui-divider last:border-b-0 hover:bg-ui-hover transition-colors ${
          isCompleted ? 'opacity-60' : ''
        }`}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        <div className="flex items-start gap-2">
          {/* Checkbox */}
          <button
            onClick={() => onToggleTodo(todo)}
            className={`mt-0.5 flex-shrink-0 transition-colors ${
              isCompleted
                ? 'text-ui-green-fg hover:text-ui-green-fg'
                : 'text-ui-muted hover:text-ui-ink-3'
            }`}
          >
            {isCompleted ? (
              <CheckCircle2 className="w-5 h-5" />
            ) : (
              <Circle className="w-5 h-5" />
            )}
          </button>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className={`text-sm font-medium ${isCompleted ? 'line-through text-ui-muted' : 'text-ui-ink'}`}>
              {todo.title}
            </div>

            {/* Meta info */}
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {/* Priority badge */}
              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${getPriorityColor(todo.priority)}`}>
                {todo.priority || 'medium'}
              </span>

              {/* Due date */}
              {todo.due_date && (
                <span className={`text-xs flex items-center gap-1 ${
                  isOverdue ? 'text-ui-red-fg font-medium' : 'text-ui-muted'
                }`}>
                  {isOverdue ? (
                    <AlertCircle className="w-3 h-3" />
                  ) : (
                    <Clock className="w-3 h-3" />
                  )}
                  {formatDueDate(todo.due_date)}
                  {isOverdue && ' - Overdue'}
                </span>
              )}

              {/* Assigned to */}
              {todo.assigned_to_name && (
                <span className="text-xs text-ui-muted">
                  {todo.assigned_to_name}
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          <AnimatePresence>
            {showActions && !isCompleted && (
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="flex items-center gap-1"
              >
                <button
                  onClick={() => onEditTodo(todo)}
                  className="p-1 text-ui-muted hover:text-ui-ink-3 hover:bg-ui-hover rounded transition-colors"
                  title="Edit todo"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDeleteTodo(todo)}
                  className="p-1 text-ui-muted hover:text-ui-red-fg hover:bg-ui-red-bg rounded transition-colors"
                  title="Delete todo"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-ui-overlay/20 z-[60]"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full sm:w-96 bg-ui-surface shadow-2xl z-[70] flex flex-col"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-ui-line flex items-center justify-between bg-ui-sunk">
              <div>
                <h2 className="text-lg font-semibold text-ui-ink">Todos</h2>
                <p className="text-sm text-ui-muted">
                  {pendingTodos.length} pending{completedTodos.length > 0 && `, ${completedTodos.length} completed`}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-ui-line rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-ui-muted" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ui-indigo-solid" />
                </div>
              ) : todos.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <div className="w-12 h-12 rounded-full bg-ui-hover flex items-center justify-center mb-3">
                    <Check className="w-6 h-6 text-ui-muted" />
                  </div>
                  <p className="text-ui-ink-3 font-medium">No todos yet</p>
                  <p className="text-sm text-ui-muted mt-1">Create your first todo to get started</p>
                </div>
              ) : (
                <>
                  {/* Pending Todos */}
                  {pendingTodos.length > 0 && (
                    <div>
                      {pendingTodos.map(todo => (
                        <TodoItem key={todo.id} todo={todo} />
                      ))}
                    </div>
                  )}

                  {/* Completed Todos */}
                  {completedTodos.length > 0 && (
                    <div className="border-t border-ui-line mt-2">
                      <div className="px-3 py-2 text-xs font-medium text-ui-muted uppercase tracking-wider bg-ui-sunk">
                        Completed ({completedTodos.length})
                      </div>
                      {completedTodos.map(todo => (
                        <TodoItem key={todo.id} todo={todo} />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Quick Add Footer */}
            <div className="border-t border-ui-line p-3 bg-ui-sunk">
              {isAddingTodo ? (
                <form onSubmit={handleQuickAdd} className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={newTodoTitle}
                    onChange={(e) => setNewTodoTitle(e.target.value)}
                    placeholder="What needs to be done?"
                    className="flex-1 px-3 py-2 text-sm border border-ui-control rounded-lg focus:outline-none focus:ring-2 focus:ring-ui-indigo-solid focus:border-transparent"
                    autoFocus
                  />
                  <button
                    type="submit"
                    disabled={!newTodoTitle.trim()}
                    className="p-2 bg-ui-indigo-strong text-ui-on-primary rounded-lg hover:bg-ui-indigo-strong/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => { setIsAddingTodo(false); setNewTodoTitle(''); }}
                    className="p-2 text-ui-muted hover:bg-ui-line rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </form>
              ) : (
                <button
                  onClick={() => setIsAddingTodo(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-ui-indigo-fg bg-ui-indigo-bg hover:bg-ui-indigo-line rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Todo
                </button>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TodoSlidePanel;
